import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';

type Category = 'Todos' | 'Hormigón Impreso' | 'Hormigón Desactivado' | 'Hormigón Pulido' | 'Hormigón Rallado' | 'Piscinas Arelite' | 'Antes y Después' | 'Protección de Acabados';

interface MediaFile {
  id: string;
  category: Category;
  location: string;
  url: string;
  type: 'image' | 'video' | 'pdf';
  name: string;
}

const ProjectsGallery = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryParam = searchParams.get('category') as Category | null;
  
  const [activeCategory, setActiveCategory] = useState<Category>(categoryParam || 'Todos');
  const [selectedMedia, setSelectedMedia] = useState<MediaFile | null>(null);
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
  const [loading, setLoading] = useState(true);

  const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
  const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

  // Inițializare client Supabase
  const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

  const categories: Category[] = [
    'Todos',
    'Hormigón Impreso',
    'Hormigón Desactivado',
    'Hormigón Pulido',
    'Hormigón Rallado',
    'Piscinas Arelite',
    'Antes y Después',
    'Protección de Acabados',
  ];

  // Mapare foldere Supabase la categorii
  const folderMapping: Record<string, { category: Category; location: string }> = {
    'Impreso': { category: 'Hormigón Impreso', location: 'Estepona' },
    'Dezactivado': { category: 'Hormigón Desactivado', location: 'Costa del Sol' },
    'Pulido': { category: 'Hormigón Pulido', location: 'Marbella' },
    'Rallado': { category: 'Hormigón Rallado', location: 'Estepona' },
    'Arelite': { category: 'Piscinas Arelite', location: 'Costa del Sol' },
    'Antes-Despues': { category: 'Antes y Después', location: 'Transformación' },
    'Proteccion': { category: 'Protección de Acabados', location: 'Aplicación' },
  };

  useEffect(() => {
    loadMediaFromSupabase();
  }, []);

  // Actualizează categoria când se schimbă parametrul URL
  useEffect(() => {
    if (categoryParam && categories.includes(categoryParam)) {
      setActiveCategory(categoryParam);
    }
  }, [categoryParam]);

  const loadMediaFromSupabase = async () => {
    try {
      console.log('🔄 Începe încărcarea fișierelor din Supabase...');
      const allMedia: MediaFile[] = [];

      const folders = ['Impreso', 'Dezactivado', 'Pulido', 'Rallado', 'Arelite', 'Antes-Despues', 'Proteccion'];

      for (const folder of folders) {
        const mapping = folderMapping[folder];
        if (!mapping) continue;

        try {
          console.log(`📂 Încărcare folder: ${folder}`);
          
          // Folosește clientul Supabase pentru a lista fișierele
          const { data: files, error } = await supabase.storage
            .from('HormiPremium')
            .list(folder, {
              limit: 100,
              offset: 0,
              sortBy: { column: 'name', order: 'asc' }
            });

          if (error) {
            console.error(`❌ Eroare la încărcarea ${folder}:`, error);
            continue;
          }

          if (!files || files.length === 0) {
            console.warn(`⚠️ Niciun fișier găsit în ${folder}`);
            continue;
          }

          console.log(`✅ Găsite ${files.length} fișiere în ${folder}`);

          files.forEach((file, index) => {
            // Ignoră placeholder-uri și directoare
            if (file.name === '.emptyFolderPlaceholder' || !file.id) {
              return;
            }

            const fileExt = file.name.split('.').pop()?.toLowerCase();
            let fileType: 'image' | 'video' | 'pdf' = 'image';

            // Detectare tip fișier
            if (['mp4', 'mov', 'avi', 'webm', 'mkv'].includes(fileExt || '')) {
              fileType = 'video';
            } else if (fileExt === 'pdf') {
              fileType = 'pdf';
            }

            // Obține URL-ul public al fișierului
            const { data: urlData } = supabase.storage
              .from('HormiPremium')
              .getPublicUrl(`${folder}/${file.name}`);

            if (urlData?.publicUrl) {
              allMedia.push({
                id: `${folder}-${index}-${file.name}`,
                category: mapping.category,
                location: mapping.location,
                url: urlData.publicUrl,
                type: fileType,
                name: file.name,
              });
            }
          });
        } catch (error) {
          console.error(`❌ Eroare la procesarea ${folder}:`, error);
        }
      }

      console.log(`✅ Total fișiere încărcate: ${allMedia.length}`);
      console.log('📊 Detalii:', allMedia);
      
      setMediaFiles(allMedia);
      setLoading(false);
    } catch (error) {
      console.error('❌ Eroare generală la încărcarea media:', error);
      setLoading(false);
    }
  };

  const handleCategoryChange = (category: Category) => {
    setActiveCategory(category);
    if (category === 'Todos') {
      setSearchParams({});
    } else {
      setSearchParams({ category });
    }
  };

  const filteredMedia = activeCategory === 'Todos' 
    ? mediaFiles 
    : mediaFiles.filter(media => media.category === activeCategory);

  const getFileIcon = (type: string) => {
    switch (type) {
      case 'video':
        return 'ri-play-circle-line';
      case 'pdf':
        return 'ri-file-pdf-line';
      default:
        return 'ri-image-line';
    }
  };

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => handleCategoryChange(category)}
              className={`px-6 py-2.5 rounded-full font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeCategory === category
                  ? 'bg-primary text-white'
                  : 'border border-gray-300 text-secondary hover:border-primary hover:text-primary'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
            <p className="mt-4 text-secondary">Cargando proyectos...</p>
          </div>
        ) : (
          <>
            <div className="columns-1 md:columns-2 lg:columns-3 gap-6 space-y-6">
              {filteredMedia.map((media) => (
                <div
                  key={media.id}
                  className="break-inside-avoid group cursor-pointer"
                  onClick={() => setSelectedMedia(media)}
                >
                  <div className="relative rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-shadow">
                    <div className="h-80">
                      {media.type === 'image' ? (
                        <img
                          src={media.url}
                          alt={`${media.category} - ${media.location}`}
                          className="w-full h-full object-cover object-top"
                        />
                      ) : media.type === 'video' ? (
                        <video
                          src={media.url}
                          className="w-full h-full object-cover"
                          muted
                          loop
                          playsInline
                        />
                      ) : (
                        <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                          <div className="text-center">
                            <i className="ri-file-pdf-line text-6xl text-red-500 mb-4"></i>
                            <p className="text-sm text-secondary px-4">{media.name}</p>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                        <p className="text-sm font-medium mb-1">{media.category}</p>
                        <p className="text-lg font-bold">{media.location}</p>
                      </div>
                      <div className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-full">
                        <i className={`${getFileIcon(media.type)} text-white text-xl`}></i>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {filteredMedia.length === 0 && (
              <div className="text-center py-20">
                <i className="ri-folder-open-line text-6xl text-gray-300 mb-4"></i>
                <p className="text-secondary">No hay proyectos en esta categoría</p>
              </div>
            )}
          </>
        )}

        {/* Botones Ver Más */}
        {activeCategory === 'Piscinas Arelite' && (
          <div className="text-center mt-12">
            <Link
              to="/servicios/piscinas-arelite"
              className="inline-flex items-center gap-3 px-8 py-4 bg-teal-600 text-white rounded-full font-semibold hover:bg-teal-700 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-information-line text-xl"></i>
              <span>Ver más información sobre Arelite</span>
              <i className="ri-arrow-right-line text-xl"></i>
            </Link>
          </div>
        )}

        {activeCategory === 'Hormigón Desactivado' && (
          <div className="text-center mt-12">
            <Link
              to="/servicios/hormigon-desactivado"
              className="inline-flex items-center gap-3 px-8 py-4 bg-primary text-white rounded-full font-semibold hover:bg-[#1873CC] transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-information-line text-xl"></i>
              <span>Ver más información sobre Hormigón Desactivado</span>
              <i className="ri-arrow-right-line text-xl"></i>
            </Link>
          </div>
        )}

        {activeCategory === 'Hormigón Rallado' && (
          <div className="text-center mt-12">
            <Link
              to="/servicios/hormigon-rallado"
              className="inline-flex items-center gap-3 px-8 py-4 bg-amber-600 text-white rounded-full font-semibold hover:bg-amber-700 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-information-line text-xl"></i>
              <span>Ver más información sobre Hormigón Rallado</span>
              <i className="ri-arrow-right-line text-xl"></i>
            </Link>
          </div>
        )}

        {activeCategory === 'Protección de Acabados' && (
          <div className="text-center mt-12">
            <Link
              to="/servicios/protecciones-acabados"
              className="inline-flex items-center gap-3 px-8 py-4 bg-teal-500 text-white rounded-full font-semibold hover:bg-teal-600 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-information-line text-xl"></i>
              <span>Ver más información sobre Protecciones</span>
              <i className="ri-arrow-right-line text-xl"></i>
            </Link>
          </div>
        )}
      </div>

      {/* Modal de Media */}
      {selectedMedia && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedMedia(null)}
        >
          <button
            className="absolute top-4 right-4 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-all cursor-pointer z-10"
            onClick={() => setSelectedMedia(null)}
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
          
          <div className="max-w-6xl w-full" onClick={(e) => e.stopPropagation()}>
            {selectedMedia.type === 'image' ? (
              <img
                src={selectedMedia.url}
                alt="Vista ampliada"
                className="max-w-full max-h-[90vh] object-contain rounded-lg mx-auto"
              />
            ) : selectedMedia.type === 'video' ? (
              <video
                src={selectedMedia.url}
                controls
                autoPlay
                className="max-w-full max-h-[90vh] rounded-lg mx-auto"
              />
            ) : (
              <div className="bg-white rounded-lg p-8 text-center">
                <i className="ri-file-pdf-line text-8xl text-red-500 mb-4"></i>
                <p className="text-xl font-bold text-dark mb-4">{selectedMedia.name}</p>
                <a
                  href={selectedMedia.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-red-500 text-white rounded-full font-semibold hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-download-line text-xl"></i>
                  Descargar PDF
                </a>
              </div>
            )}
            
            <div className="text-center mt-4 text-white">
              <p className="text-lg font-semibold">{selectedMedia.category}</p>
              <p className="text-sm opacity-80">{selectedMedia.location}</p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default ProjectsGallery;
